import { Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { VendorStore } from '@apps/mtjr/vendors/app/vendor/list/vendors.page';
import {
  InfiniteScrollCustomEvent,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonList,
  IonMenuButton,
  IonPopover,
  IonSearchbar,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { VxCurrencyPipe } from '@libs/core/lib/pipes/mtjr-currency.pipe';

import { DatePipe } from '@angular/common';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { VxDynamicImageComponent, VxImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { initialState } from '@libs/core/lib/state/async-states';
import { withOrdersList } from '@libs/mtjr/shared/generated/entities/order/order-store-features';
import { SubOrderStatusList } from '@libs/mtjr/shared/generated/types';
import { OrderStatusTextComponent } from '@libs/mtjr/shared/orders/order-status-text.component';
import { patchState, signalStore, withComputed, withMethods } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';

export const OrdersStore = signalStore(
  withOrdersList(),
  withComputed((store) => ({
    splitedOrders: computed(() => {
      const splitedOrders = store.orders().flatMap((order) => {
        return order.subOrders.flatMap((subOrder) => {
          return {
            ...order,
            subOrders: [subOrder],
          };
        });
      });
      return splitedOrders;
    }),
  })),
  withMethods((store) => ({
    reloadOrdersList() {
      patchState(store, {
        ordersListState: initialState(),
        ordersListInput: { page: 1, totalPerPage: 20 },
        ordersListCanFetchNext: false,
        ordersListFetchingNext: false,
      });
    },
  })),
);

@Component({
  selector: 'mav-orders',
  providers: [OrdersStore],
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonContent,
    IonSearchbar,
    IonCard,
    IonText,
    IonLabel,
    IonButton,
    IonIcon,
    RouterLink,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    OrderStatusTextComponent,
    VxCurrencyPipe,
    IonList,
    IonPopover,
    VxLoadingComponent,
    VxErrorComponent,
    IonItem,
    VxImageComponent,
    VxDynamicImageComponent,
    TranslatePipe,
    DatePipe,
    IonMenuButton,
  ],
  templateUrl: './orders.page.html',
})
export class OrdersPage {
  store = inject(OrdersStore);
  vendorStore = inject(VendorStore);
  orderStatusList = SubOrderStatusList;
  route = inject(ActivatedRoute);
  router = inject(Router);
  searchValue = signal<string | undefined>(undefined);
  ionPopover = viewChild(IonPopover, { read: IonPopover });
  vendors = this.vendorStore.vendors;

  constructor() {
    effect(() => {
      const query = this.store.ordersListInput();
      this.router.navigate([], {
        queryParams: {
          status: query.status,
          search: query.search ? query.search : undefined,
        },
      });
    });

    this.route.queryParams.subscribe((params) => {
      const { status, search } = params;
      this.searchValue.set(search);
      const query = this.store.ordersListInput();
      if (this.store.ordersListState().type !== 'initial' && status === query.status && search === query.search) return;

      this.store.ordersListPage({ status, search });
    });
  }

  ionViewWillLeave() {
    this.ionPopover()?.dismiss();
  }

  async onIonInfinite(event: InfiniteScrollCustomEvent) {
    await this.store.ordersListFetchNextPage();
    await event?.target?.complete();
  }

  handleSearchInput(event?: CustomEvent) {
    const search = event?.detail?.value;
    if (this.store.ordersListInput().search === search) return;
    this.store.ordersListPage({ search, page: 1 });
  }

  reloadOrdersList() {
    const status = this.store.ordersListInput().status;
    const search = this.store.ordersListInput().search;
    this.store.reloadOrdersList();
    this.store.ordersListPage({ status, search });
  }
}
